import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { statoPiano, moduloIncluso, PIANI_INFO, MODULI, LISTINO } from "./pianiNexiora";
import { useFornitore, urlLegali } from "./fornitore";

/**
 * usePiano — stato del piano dell'organizzazione indicata (anche quando il Platform
 * Owner "entra" in un'altra organizzazione). Legge SOLO il documento organizations/{id}
 * tramite pianiNexiora.js, la fonte unica di listino e regole.
 *
 * Fino alla W300 esisteva una seconda fonte (collezione "subscriptions", con una propria
 * data di fine prova creata al primo accesso e piani "base"/"business"): poteva dare una
 * scadenza diversa da quella mostrata altrove. Non viene più letta né creata.
 *
 * I nomi delle funzioni usati dalle pagine (pianificazione, economia, insight, noteSpese,
 * rapportini, acquisti) sono mappati sui moduli del listino.
 */
const FUNZIONE_MODULO = {
  pianificazione: "squadra",
  economia: "economico",
  insight: "insight",
  noteSpese: "economico",
  rapportini: "documenti",
  acquisti: "acquisti",
};

export function usePiano(orgId) {
  const { isPlatformOwner } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db, "organizations", orgId),
      snap => { setOrg(snap.exists() ? snap.data() : null); setLoading(false); },
      () => setLoading(false));
  }, [orgId]);

  const stato = statoPiano(org, { isPlatformOwner });
  const pe = stato.pianoEffettivo;

  const limiti = {};
  Object.entries(FUNZIONE_MODULO).forEach(([f, m]) => {
    limiti[f] = stato.bloccoTotale ? "readonly" : (moduloIncluso(m, pe) ? "full" : "readonly");
  });

  const info = pe === "trial"
    ? { id: "trial", label: "Prova gratuita", colore: "#059669", bg: "#d1fae5", emoji: "🎁" }
    : PIANI_INFO[pe];
  const piano = { ...info, limiti };

  return {
    loading, stato, piano, pianoEffettivo: pe, limiti,
    giorniRimasti: stato.giorniProva ?? LISTINO.giorniProva,
    puoUsare: (f) => limiti[f] === "full",
    solaLettura: (f) => limiti[f] === "readonly",
    isTrial: pe === "trial",
    isFree: pe === "free",
    isBase: pe === "free",          // nome storico usato dalla Sidebar
    isBusiness: pe === "pro" || pe === "gold",
    trialScaduto: stato.provaScaduta,
    PIANI: PIANI_INFO,
  };
}

/** Badge accanto ai moduli: giorni di prova rimasti, o piano richiesto se in sola lettura. */
export function BadgePiano({ funzione, piano, giorniRimasti }) {
  if (!piano) return null;
  const stato = piano.limiti?.[funzione];
  if (stato === "full" && piano.id === "trial" && giorniRimasti <= 14) {
    return (
      <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:8,background:"#fef3c7",color:"#d97706",display:"inline-flex",alignItems:"center",gap:3}}>
        🎁 {giorniRimasti}gg di prova
      </span>
    );
  }
  if (stato === "readonly") {
    const richiesto = PIANI_INFO[MODULI[FUNZIONE_MODULO[funzione]]?.piano || "pro"].label;
    return (
      <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:8,background:"#fee2e2",color:"#dc2626",display:"inline-flex",alignItems:"center",gap:3}}>
        🔒 Sola lettura — piano {richiesto}
      </span>
    );
  }
  return null;
}

/** Banner quando si tenta di usare un modulo non incluso nel piano. */
export function BannerUpgrade({ funzione, onClose }) {
  const fornitore = useFornitore();
  const modulo = FUNZIONE_MODULO[funzione];
  const nome = MODULI[modulo]?.label || funzione;
  const richiesto = PIANI_INFO[MODULI[modulo]?.piano || "pro"].label;
  return (
    <div style={{position:"fixed",top:20,right:20,zIndex:99999,background:"#fff",border:"2px solid #7c3aed",
      borderRadius:14,padding:20,maxWidth:340,boxShadow:"0 8px 40px rgba(124,58,237,.2)",animation:"slideInRight .2s ease"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:10}}>
        <div style={{fontSize:15,fontWeight:800,color:"#111827"}}>🔒 {nome} — piano {richiesto}</div>
        {onClose&&<button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:16}}>✕</button>}
      </div>
      <p style={{fontSize:12,color:"#6b7280",lineHeight:1.6,marginBottom:14}}>
        Questo modulo è incluso dal piano {richiesto}. I dati già inseriti restano consultabili in sola lettura:
        per tornare a crearli e modificarli attiva il piano {richiesto}.
      </p>
      <a href={urlLegali(fornitore).prezzi} target="_blank" rel="noreferrer"
        style={{display:"block",padding:"8px",background:"#7c3aed",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,textAlign:"center",textDecoration:"none",fontFamily:"'Inter',sans-serif"}}>
        Vedi piani e prezzi →
      </a>
      <div style={{fontSize:10,color:"#9ca3af",marginTop:8,textAlign:"center"}}>I tuoi dati restano consultabili ed esportabili.</div>
    </div>
  );
}
